import { Injectable, WritableSignal, inject, signal } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { Location, LocationsByCursorQuery } from '@generated/sammathnaur.graphql'
import { Subject } from 'rxjs'
import { AssetsTreeService } from './assets-tree.service'
import { LocationsService } from '../../../core/api/locations/locations.service'
import { delay } from '../../../core/others/delay'
import { TreeViewItem } from '../sections/tree-section/components/tree-view/tree-view.type'
import { TreeSectionService } from '../sections/tree-section/tree-section.service'

@Injectable({ providedIn: 'root' })
export class AssetsTreeFilterService {
  public isEmpty = signal(false)
  public isLoading = signal(true)
  public isLoadingMore = signal(false)
  public totalCount = signal(0)
  public isEmptyFiltered = signal(false)
  public locations: WritableSignal<TreeViewItem[]> = signal([])
  public isFiltering = signal(false)

  public form = new FormGroup({
    nameContains: new FormControl(),
    locationGroup: new FormControl(),
    warrantyStartDateLte: new FormControl(),
    warrantyEndDateGte: new FormControl(),
    warrantyStatusIn: new FormControl()
  })


  public searched$ = new Subject<void>()
  public locationsService = inject(LocationsService)
  public assetsTreeService = inject(AssetsTreeService)
  public treeSectionService = inject(TreeSectionService)

  public clearForm(): void {
    this.form.reset()
  }

  public reset(): void {
    this.totalCount.set(0)
    this.isEmpty.set(false)
    this.isLoading.set(true)
    this.isLoadingMore.set(false)
    this.isEmptyFiltered.set(false)
    this.locations.set([])
  }

  public async search(isScrolling?: boolean): Promise<void> {
    const isFiltering = this.form.get('nameContains')?.value || this.form.get('locationGroup')?.value || this.form.get('warrantyStartDateLte')?.value || this.form.get('warrantyEndDateGte')?.value || this.form.get('warrantyStatusIn')?.value

    if (!isScrolling) {
      this.isLoading.set(true)
      this.locationsService.after.set(null)
      this.assetsTreeService.selectedItem.set(null)
      this.assetsTreeService.closeDetails$.next()
    }

    this.isLoadingMore.set(Boolean(isScrolling))

    this.locationsService.filterForm.patchValue({
      archived: 'all',
      sectorEnvironmentEquipmentNameOrNumberContains: this.form.get('nameContains')?.value,
      locationGroupId_in: this.form.get('locationGroup')?.value?.id ? [this.form.get('locationGroup')?.value?.id] : [],
      warrantyStartDateLte: this.form.get('warrantyStartDateLte')?.value,
      warrantyEndDateGte: this.form.get('warrantyEndDateGte')?.value,
      warrantyStatusIn: this.form.get('warrantyStatusIn')?.value
    })

    const result = await this.locationsService.searchPage()
    await delay(500)

    const { locationsByCursor } = result.data as LocationsByCursorQuery
    this.locationsService.after.set(locationsByCursor?.pageInfo?.endCursor || null)

    if (!isScrolling) {
      this.isEmpty.set(!locationsByCursor?.totalCount && !isFiltering)
      this.isEmptyFiltered.set(!locationsByCursor?.totalCount && isFiltering)
    }

    this.totalCount.set(locationsByCursor?.totalCount || 0)
    this.searched$.next()

    this.locations.set(this.treeSectionService.toLocationTreeViewItems({
      currentLocations: isScrolling ? this.locations() : [],
      newLocations: (locationsByCursor?.items || []) as Location[]
    }))

    this.isLoading.set(false)
    this.isLoadingMore.set(false)
  }

  public clearFilters(): void {
    this.form.get('warrantyStartDateLte')?.reset()
    this.form.get('warrantyEndDateGte')?.reset()
    this.form.get('warrantyStatusIn')?.reset()
    this.isFiltering.set(false)
  }
}
